import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  Link
} from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import Menu from './components/Menu';
import Cart from './components/Cart';
import DeliveryOption from './components/DeliveryOption';
import { CartProvider } from './context/CartContext';
import { AdminLogin, Dashboard } from './components/admin';
import { isAdminUser } from './components/admin/adminAuth';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';

interface LayoutProps {
  children: React.ReactNode;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

type AuthGateState = 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Guards /admin/dashboard/* with real Firebase Auth state plus the admin
 * email allowlist. The previous implementation only read
 * `localStorage.adminAuth`, which anyone could set from DevTools to reach
 * the dashboard. RTDB security rules remain the real data barrier; this is
 * a routing gate, not a security boundary.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthGateState>('loading');

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (isAdminUser(user)) {
        setAuthState('authenticated');
      } else {
        // Signed out (or signed in with a non-admin account): clear the
        // legacy flag so it can never go stale, and bounce to the login.
        localStorage.removeItem('adminAuth');
        setAuthState('unauthenticated');
      }
    });
    return unsubscribe;
  }, []);

  if (authState === 'loading') {
    return <LoadingSpinner />;
  }

  // Navigate directly instead of rendering null (avoids a blank frame).
  return authState === 'authenticated' ? <>{children}</> : <Navigate to="/admin" replace />;
};

// Create a Layout component to wrap non-admin pages
const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <>
      <Header />
      <main>
        {children}
      </main>
      <Footer />
      {/* Cart drawer instance — mounted exactly once per customer page. */}
      <Cart />
    </>
  );
};

const AdminRoutes = () => (
  <Routes>
    <Route path="/" element={<AdminLogin />} />
    <Route
      path="/dashboard/*"
      element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      }
    />
    {/* Catch-all: without it, /admin/<anything-else> rendered a blank page. */}
    <Route path="*" element={<Navigate to="/admin" replace />} />
  </Routes>
);

const CustomerRoutes = () => {
  const navigate = useNavigate();

  const handleDeliverySelect = (option: 'surplace' | 'livraison') => {
    localStorage.setItem('deliveryOption', option);
    navigate('/menu');
  };

  return (
    <Routes>
      <Route path="/" element={
        <DeliveryOption onSelect={handleDeliverySelect} />
      } />
      <Route path="/menu" element={
        <RequireDeliveryOption>
          <Layout>
            <Menu />
          </Layout>
        </RequireDeliveryOption>
      } />
      {/* NOTE: no /cart route on purpose. The cart is a drawer rendered once
          by Layout (toggled from the Header); a separate /cart page mounted a
          second Cart instance, duplicating its window error listener and QR
          param effect (the double toggleCart() call cancelled itself out).
          Nothing in the app linked to /cart — it was an orphan route. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

/**
 * UX gate (NOT a security gate): ensures visitors pick "sur place" or
 * "livraison" on the landing page before seeing the menu. It only reads a
 * preference from localStorage; it protects no data. Returning <Navigate>
 * directly (instead of an effect + null) avoids a blank first frame.
 */
const RequireDeliveryOption: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const deliveryOption = localStorage.getItem('deliveryOption');

  if (!deliveryOption) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const NotFound = () => (
  <Layout>
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
        <p className="text-gray-600 mb-8">Page not found</p>
        <Link to="/" className="text-primary hover:text-primary-dark">
          Return Home
        </Link>
      </div>
    </div>
  </Layout>
);

const App: React.FC = () => {
  return (
    <CartProvider>
      <Router>
        <Routes>
          <Route path="/admin/*" element={<AdminRoutes />} />
          <Route path="/*" element={<CustomerRoutes />} />
        </Routes>
      </Router>
    </CartProvider>
  );
};

export default App;
